import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";

import {
	CancelBulkOrderDeleteJobMutation,
	RequestBulkOrderDeleteMutation
} from "~/gql/mutations/fulfill/bulkOrderDeleteJob.generated";
import { ScheduleMasterOrderWeekMutation } from "~/gql/mutations/masterOrders.generated";
import {
	AddLineItemsMutation,
	AddLineItemsWithPricingMutation,
	AddOrderFlatChargeMutation,
	AddOrderLineUpgradeMutation,
	AllocateLineItemsMutation,
	BulkAllocateOrdersMutation,
	ClearOrderDropShipDestinationMutation,
	ClearOrderNoTaxMutation,
	ClearOrderNoteMutation,
	CorrectAcknowledgedQuantityMutation,
	CorrectAcknowledgedQuantityMutationVariables,
	CorrectOrderMutation,
	CreditExistingOrderMutation,
	DeleteOrderMutation,
	EditOrderFlatChargeMutation,
	EditOrderMutation,
	ImportOrdersMutation,
	InitializeOrderMutation,
	OverrideOrderLineUpgradePricingMutation,
	PlaceOrderHoldMutation,
	RecordOrderDepositMutation,
	RecordOrderDepositMutationVariables,
	RefreshOrderTaxEstimateMutation,
	ReleaseOrderHoldMutation,
	RemoveLineItemAllocationsMutation,
	RemoveLineItemMutation,
	RemoveOrderFlatChargeMutation,
	RemoveOrderLineUpgradeMutation,
	RepriceOrderLineMutation,
	RepriceOrderMutation,
	ResetOrderSyncStatusMutation,
	SetLineItemCustomerItemMutation,
	SetLineItemOverridePriceMutation,
	SetLineItemOverridePriceMutationVariables,
	SetLineItemShippingUomMutation,
	SetOrderContainerMutation,
	SetOrderDropShipDestinationMutation,
	SetOrderNoTaxMutation,
	SetOrderNoteMutation,
	SetOrderPlannedShipDateMutation,
	SetOrderQuoteDetailsMutation,
	SetOrderShipFromSiteMutation,
	UnshipOrderMutation,
	UpdateAcknowledgedQuantityMutation,
	UpdateAndGetNextOrderNumberMutation,
	UpdateLineItemContextNoteMutation,
	UpdateLineItemMutation,
	UpdateLineItemMutationVariables,
	UpdateOrderContactMutation,
	UpdateOrderSendInvoiceEmailsMutation,
	UpdateOrderSendOrderEmailsMutation,
	UpdateOrderStatusMutation,
	UpdateSalesPersonMutation,
	UpdateShippedQuantityMutation,
	UpdateShippedQuantityMutationVariables
} from "~/gql/mutations/orders.generated";
import { GetCombinedOrdersQuery, GetRecentCombinedOrdersQuery } from "~/gql/queries/combinedOrders.generated";
import {
	GetEarlierCreditsQuery,
	GetMyQuoteFollowUpsDueQuery,
	GetOrderContainerRequirementQuery,
	GetOrderContainerRequirementQueryVariables,
	GetOrderContainerRequirementsQuery,
	GetOrderCreditsQuery,
	GetOrderCreditsQueryVariables,
	GetOrderDeliveryBillingLinesQuery,
	GetOrderDeliveryBillingLinesQueryVariables,
	GetOrderDeliveryStatusQuery,
	GetOrderDeliveryStatusQueryVariables,
	GetOrderDepositStatusQuery,
	GetOrderDepositStatusQueryVariables,
	GetOrderDetailsPdfQuery,
	GetOrderMinimumOverridesQuery,
	GetOrderMinimumOverridesQueryVariables,
	GetOrderPaymentsQuery,
	GetOrderPaymentsQueryVariables,
	GetOrderQuery,
	GetOrderQueryVariables,
	GetOrderQuoteDetailsQuery,
	GetOrderQuoteDetailsQueryVariables,
	GetOrdersForAllocationMoveQuery,
	GetOrdersGridListQuery,
	GetOrdersQuery,
	GetOrderVerificationQuery,
	GetOrderVerificationQueryVariables,
	OrderDetailsFragment
} from "~/gql/queries/order.generated";
import showsOrderTag from "~/services/endpoints/orderShowsTag";

const importApiEndpointsLazilyToBreakCycle = async () =>
	(await import("~/services/endpoints/apiEndpoints")).apiEndpoints;

interface OrderPayloadResult {
	errors?: ReadonlyArray<{ code?: null | string; message: string }> | null;
	order?: OrderDetailsFragment | null;
}

type PayloadField<TData> = Exclude<keyof TData, "__typename"> & string;

type OrderPayloadMutation<TData> = { [P in PayloadField<TData>]: OrderPayloadResult | null | undefined };

interface ReturnedOrderLifecycle<TData> {
	dispatch: <ReturnValue, State>(thunkAction: ThunkAction<ReturnValue, State, unknown, UnknownAction>) => ReturnValue;
	queryFulfilled: Promise<{ data: TData }>;
}

const orderTag = (orderId: string) => ({ id: orderId, type: "Orders" as const });

const showsOrders = (orderIds: ReadonlyArray<null | string | undefined>) =>
	orderIds.filter((orderId): orderId is string => Boolean(orderId)).map(showsOrderTag);

const providesOrderList = (orderIds: ReadonlyArray<null | string | undefined>) => [
	"Orders" as const,
	...showsOrders(orderIds)
];

const returnedOrderOf = (payload: OrderPayloadResult | null | undefined) =>
	payload?.errors?.length ? null : (payload?.order ?? null);

const writesReturnedOrder = <TData extends OrderPayloadMutation<TData>, TArg>(
	payloadField: PayloadField<TData>,
	orderIdOf: (arg: TArg) => string
) => {
	const payloadOf = (data: TData | undefined): OrderPayloadResult | null | undefined => data?.[payloadField];
	return {
		invalidatesTags: (result: TData | undefined, error: unknown, arg: TArg) =>
			error || !returnedOrderOf(payloadOf(result)) ? [orderTag(orderIdOf(arg))] : [showsOrderTag(orderIdOf(arg))],
		async onQueryStarted(arg: TArg, { dispatch, queryFulfilled }: ReturnedOrderLifecycle<TData>) {
			try {
				const order = returnedOrderOf(payloadOf((await queryFulfilled).data));
				if (!order) return;
				const apiEndpoints = await importApiEndpointsLazilyToBreakCycle();
				dispatch(
					apiEndpoints.util.updateQueryData("GetOrder", { orderId: orderIdOf(arg) }, (draft) => {
						draft.order = order;
					})
				);
			} catch {}
		}
	};
};

export const ordersEndpoints = {
	AddLineItems: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: AddLineItemsMutation) => res
	},
	AddLineItemsWithPricing: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: AddLineItemsWithPricingMutation) => res
	},
	AddOrderLineUpgrade: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: AddOrderLineUpgradeMutation) => res
	},
	AllocateLineItems: {
		invalidatesTags: ["Orders", "Learning"] as AddTagTypes,
		transformResponse: (res: AllocateLineItemsMutation) => res
	},
	BulkAllocateOrders: {
		invalidatesTags: ["Orders", "Learning"] as AddTagTypes,
		transformResponse: (res: BulkAllocateOrdersMutation) => res
	},
	ClearOrderDropShipDestination: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: ClearOrderDropShipDestinationMutation) => res
	},
	ClearOrderNote: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: ClearOrderNoteMutation) => res
	},
	ClearOrderNoTax: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: ClearOrderNoTaxMutation) => res
	},
	CorrectAcknowledgedQuantity: {
		...writesReturnedOrder<CorrectAcknowledgedQuantityMutation, CorrectAcknowledgedQuantityMutationVariables>(
			"correctAcknowledgedQuantity",
			(arg) => arg.input.orderId
		),
		transformResponse: (res: CorrectAcknowledgedQuantityMutation) => res
	},
	CorrectOrder: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: CorrectOrderMutation) => res
	},
	ScheduleMasterOrderWeek: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: ScheduleMasterOrderWeekMutation) => res
	},
	CreditExistingOrder: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: CreditExistingOrderMutation) => res
	},
	DeleteOrder: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: DeleteOrderMutation) => res
	},
	RequestBulkOrderDelete: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: RequestBulkOrderDeleteMutation) => res
	},
	CancelBulkOrderDeleteJob: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: CancelBulkOrderDeleteJobMutation) => res
	},
	AddOrderFlatCharge: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: AddOrderFlatChargeMutation) => res
	},
	EditOrderFlatCharge: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: EditOrderFlatChargeMutation) => res
	},
	RemoveOrderFlatCharge: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: RemoveOrderFlatChargeMutation) => res
	},
	EditOrder: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: EditOrderMutation) => res
	},
	GetCombinedOrders: {
		providesTags: (result: GetCombinedOrdersQuery | undefined) => [
			...providesOrderList((result?.combinedOrders?.nodes ?? []).map((node) => node.orderEntityId)),
			"MasterOrders" as const
		],
		transformResponse: (res: GetCombinedOrdersQuery) => res
	},
	GetEarlierCredits: {
		providesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: GetEarlierCreditsQuery) => res
	},
	GetMyQuoteFollowUpsDue: {
		providesTags: ["Orders", "OrderQuoteDetails"] as AddTagTypes,
		transformResponse: (res: GetMyQuoteFollowUpsDueQuery) => res
	},
	GetOrder: {
		providesTags: (_result: GetOrderQuery | undefined, _error: unknown, arg: GetOrderQueryVariables) => [
			"Orders" as const,
			{ id: arg.orderId, type: "Orders" as const }
		],
		transformResponse: (res: GetOrderQuery) => res
	},
	GetOrderContainerRequirement: {
		providesTags: (
			_result: GetOrderContainerRequirementQuery | undefined,
			_error: unknown,
			arg: GetOrderContainerRequirementQueryVariables
		) => providesOrderList([arg.orderId]),
		transformResponse: (res: GetOrderContainerRequirementQuery) => res
	},
	GetOrderContainerRequirements: {
		providesTags: (result: GetOrderContainerRequirementsQuery | undefined) =>
			providesOrderList((result?.orderContainerRequirements ?? []).map((entry) => entry.orderId)),
		transformResponse: (res: GetOrderContainerRequirementsQuery) => res
	},
	GetOrderCredits: {
		providesTags: (
			_result: GetOrderCreditsQuery | undefined,
			_error: unknown,
			arg: GetOrderCreditsQueryVariables | void
		) => providesOrderList([arg?.where?.orderId?.eq]),
		transformResponse: (res: GetOrderCreditsQuery) => res
	},
	GetOrderDeliveryBillingLines: {
		providesTags: (
			_result: GetOrderDeliveryBillingLinesQuery | undefined,
			_error: unknown,
			arg: GetOrderDeliveryBillingLinesQueryVariables
		) => providesOrderList([arg.orderId]),
		transformResponse: (res: GetOrderDeliveryBillingLinesQuery) => res
	},
	GetOrderDeliveryStatus: {
		providesTags: (
			_result: GetOrderDeliveryStatusQuery | undefined,
			_error: unknown,
			arg: GetOrderDeliveryStatusQueryVariables
		) => providesOrderList([arg.orderId]),
		transformResponse: (res: GetOrderDeliveryStatusQuery) => res
	},
	GetOrderDepositStatus: {
		providesTags: (
			_result: GetOrderDepositStatusQuery | undefined,
			_error: unknown,
			arg: GetOrderDepositStatusQueryVariables
		) => providesOrderList([arg.where.orderId?.eq]),
		transformResponse: (res: GetOrderDepositStatusQuery) => res
	},
	GetOrderDetailsPdfBase64: {
		transformResponse: (res: GetOrderDetailsPdfQuery) => res
	},
	GetOrderMinimumOverrides: {
		providesTags: (
			_result: GetOrderMinimumOverridesQuery | undefined,
			_error: unknown,
			arg: GetOrderMinimumOverridesQueryVariables
		) => providesOrderList([arg.where.orderId?.eq]),
		transformResponse: (res: GetOrderMinimumOverridesQuery) => res
	},
	GetOrderPayments: {
		providesTags: (
			_res: GetOrderPaymentsQuery | undefined,
			_err: unknown,
			arg: GetOrderPaymentsQueryVariables | void
		) => [
			{ id: (arg && arg.where?.orderId?.eq) || "LIST", type: "OrderPayments" as const },
			...providesOrderList([arg?.where?.orderId?.eq])
		],
		transformResponse: (res: GetOrderPaymentsQuery) => res
	},
	GetOrderQuoteDetails: {
		providesTags: (
			_result: GetOrderQuoteDetailsQuery | undefined,
			_error: unknown,
			arg: GetOrderQuoteDetailsQueryVariables
		) => [...providesOrderList([arg.orderId]), "OrderQuoteDetails" as const],
		transformResponse: (res: GetOrderQuoteDetailsQuery) => res
	},
	GetOrderVerification: {
		providesTags: (
			_result: GetOrderVerificationQuery | undefined,
			_error: unknown,
			arg: GetOrderVerificationQueryVariables
		) => [...providesOrderList([arg.orderId]), "VerifyContainers" as const],
		transformResponse: (res: GetOrderVerificationQuery) => res
	},
	GetOrders: {
		providesTags: (result: GetOrdersQuery | undefined) =>
			providesOrderList((result?.orders?.nodes ?? []).map((node) => node.orderId)),
		transformResponse: (res: GetOrdersQuery) => res
	},
	GetOrdersForAllocationMove: {
		providesTags: (result: GetOrdersForAllocationMoveQuery | undefined) =>
			providesOrderList((result?.orders?.nodes ?? []).map((node) => node.orderId)),
		transformResponse: (res: GetOrdersForAllocationMoveQuery) => res
	},
	GetOrdersGridList: {
		providesTags: (result: GetOrdersGridListQuery | undefined) =>
			providesOrderList((result?.orders?.nodes ?? []).map((node) => node.orderId)),
		transformResponse: (res: GetOrdersGridListQuery) => res
	},
	GetRecentCombinedOrders: {
		providesTags: (result: GetRecentCombinedOrdersQuery | undefined) => [
			...providesOrderList((result?.combinedOrders?.nodes ?? []).map((node) => node.orderEntityId)),
			"MasterOrders" as const
		],
		transformResponse: (res: GetRecentCombinedOrdersQuery) => res
	},
	ImportOrders: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: ImportOrdersMutation) => res
	},
	InitializeOrder: {
		invalidatesTags: ["Orders", "Learning"] as AddTagTypes,
		transformResponse: (res: InitializeOrderMutation) => res
	},
	OverrideOrderLineUpgradePricing: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: OverrideOrderLineUpgradePricingMutation) => res
	},
	PlaceOrderHold: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: PlaceOrderHoldMutation) => res
	},
	RecordOrderDeposit: {
		invalidatesTags: (
			_res: RecordOrderDepositMutation | undefined,
			_err: unknown,
			arg: RecordOrderDepositMutationVariables
		) => [{ id: arg.input.orderId, type: "OrderPayments" as const }],
		transformResponse: (res: RecordOrderDepositMutation) => res
	},
	RefreshOrderTaxEstimate: {
		invalidatesTags: ["Orders", "PortalCustomerOrders"] as AddTagTypes,
		transformResponse: (res: RefreshOrderTaxEstimateMutation) => res
	},
	ReleaseOrderHold: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: ReleaseOrderHoldMutation) => res
	},
	RemoveLineItem: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: RemoveLineItemMutation) => res
	},
	RemoveLineItemAllocations: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: RemoveLineItemAllocationsMutation) => res
	},
	RemoveOrderLineUpgrade: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: RemoveOrderLineUpgradeMutation) => res
	},
	RepriceOrder: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: RepriceOrderMutation) => res
	},
	RepriceOrderLine: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: RepriceOrderLineMutation) => res
	},
	ResetOrderSyncStatus: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: ResetOrderSyncStatusMutation) => res
	},
	SetLineItemCustomerItem: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetLineItemCustomerItemMutation) => res
	},
	SetLineItemOverridePrice: {
		...writesReturnedOrder<SetLineItemOverridePriceMutation, SetLineItemOverridePriceMutationVariables>(
			"setLineItemOverridePrice",
			(arg) => arg.input.orderId
		),
		transformResponse: (res: SetLineItemOverridePriceMutation) => res
	},
	SetLineItemShippingUom: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetLineItemShippingUomMutation) => res
	},
	SetOrderContainer: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetOrderContainerMutation) => res
	},
	SetOrderPlannedShipDate: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetOrderPlannedShipDateMutation) => res
	},
	SetOrderDropShipDestination: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetOrderDropShipDestinationMutation) => res
	},
	SetOrderNote: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetOrderNoteMutation) => res
	},
	SetOrderNoTax: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetOrderNoTaxMutation) => res
	},
	SetOrderQuoteDetails: {
		invalidatesTags: ["OrderQuoteDetails"] as AddTagTypes,
		transformResponse: (res: SetOrderQuoteDetailsMutation) => res
	},
	SetOrderShipFromSite: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: SetOrderShipFromSiteMutation) => res
	},
	UnshipOrder: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: UnshipOrderMutation) => res
	},
	UpdateAcknowledgedQuantity: {
		invalidatesTags: ["Orders", "Learning"] as AddTagTypes,
		transformResponse: (res: UpdateAcknowledgedQuantityMutation) => res
	},
	UpdateAndGetNextOrderNumber: {
		invalidatesTags: ["Orders", "MasterOrders"] as AddTagTypes,
		transformResponse: (res: UpdateAndGetNextOrderNumberMutation) => res
	},
	UpdateLineItem: {
		...writesReturnedOrder<UpdateLineItemMutation, UpdateLineItemMutationVariables>(
			"updateLineItem",
			(arg) => arg.input.orderId
		),
		transformResponse: (res: UpdateLineItemMutation) => res
	},
	UpdateLineItemContextNote: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: UpdateLineItemContextNoteMutation) => res
	},
	UpdateOrderContact: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: UpdateOrderContactMutation) => res
	},
	UpdateOrderSendInvoiceEmails: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: UpdateOrderSendInvoiceEmailsMutation) => res
	},
	UpdateOrderSendOrderEmails: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: UpdateOrderSendOrderEmailsMutation) => res
	},
	UpdateOrderStatus: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: UpdateOrderStatusMutation) => res
	},
	UpdateSalesPerson: {
		invalidatesTags: ["Orders"] as AddTagTypes,
		transformResponse: (res: UpdateSalesPersonMutation) => res
	},
	UpdateShippedQuantity: {
		...writesReturnedOrder<UpdateShippedQuantityMutation, UpdateShippedQuantityMutationVariables>(
			"updateShippedQuantity",
			(arg) => arg.input.orderId
		),
		transformResponse: (res: UpdateShippedQuantityMutation) => res
	}
};
